import { containsText, hasActiveAmountFilter, matchesNumericFilter, type TableAmountFilterOp } from '@/components/tableFilterUtils'
import type { MultiSelectOption } from '@/components/MultiSelectFilter'
import { rowHasMeaningfulOverrides } from '@/components/ledgerOverrideStatus'
import type { WarehouseLedgerKind, WarehouseLedgerTransactionItem } from '@/services/warehouseTypes'
import { normalizeBulkSku } from '@/utils/normalizeBulkSku'
import { uploadsErrorVisibleForMonth } from '@/utils/completedCalendarMonth'
import { dateToReportMonth } from '@/components/uploadsMonthOptions'

export type LedgerGreyStatusId = 'upstream_changed' | 'edited'
export type LedgerRedStatusId =
  | 'missing_linked_invoice'
  | 'missing_case_cost'
  | 'missing_store'
  | 'missing_shipping_alloc'
  | 'missing_pick_fee_alloc'
  | 'missing_receiving_alloc'
  | 'unresolved_sku'
  | 'negative_balance'

export const LEDGER_RED_STATUS_IDS = new Set<LedgerRedStatusId>([
  'missing_linked_invoice',
  'missing_case_cost',
  'missing_store',
  'missing_shipping_alloc',
  'missing_pick_fee_alloc',
  'missing_receiving_alloc',
  'unresolved_sku',
  'negative_balance',
])

export function isLedgerRedStatusId(id: string): id is LedgerRedStatusId {
  return LEDGER_RED_STATUS_IDS.has(id as LedgerRedStatusId)
}

export type LedgerConcreteStatusFilterId = LedgerGreyStatusId | LedgerRedStatusId

export type LedgerSpecialStatusFilterId = 'any_flags' | 'any_errors' | 'no_flags' | 'no_errors'

export type LedgerStatusFilterId = LedgerConcreteStatusFilterId | LedgerSpecialStatusFilterId

export type LedgerSignedCasesFilterId = 'nonzero' | 'zero'

export type LedgerTableFilters = {
  status: LedgerStatusFilterId[]
  bk_txn_id: string[]
  bs_log_id: string[]
  date_from: string
  date_to: string
  product: string[]
  linked_invoice: string[]
  kind: WarehouseLedgerKind[]
  signed_cases: LedgerSignedCasesFilterId[]
  order_number: string[]
  unit_cost_op: TableAmountFilterOp
  unit_cost: string
  notes: string
}

export const EMPTY_LEDGER_TABLE_FILTERS: LedgerTableFilters = {
  status: [],
  bk_txn_id: [],
  bs_log_id: [],
  date_from: '',
  date_to: '',
  product: [],
  linked_invoice: [],
  kind: [],
  signed_cases: [],
  order_number: [],
  unit_cost_op: 'eq',
  unit_cost: '',
  notes: '',
}

export const LEDGER_LINKED_INVOICE_NONE = '__not_linked__'
export const LEDGER_LINKED_INVOICE_STORE_TRANSFER = '__no_invoice_expected__'

export const LEDGER_STATUS_LABELS: Record<LedgerConcreteStatusFilterId, string> = {
  edited: 'Edited',
  missing_case_cost: 'Missing case cost',
  missing_store: 'Missing store',
  missing_linked_invoice: 'Missing linked invoice',
  missing_pick_fee_alloc: 'Missing pick fee alloc',
  missing_receiving_alloc: 'Missing receiving alloc',
  missing_shipping_alloc: 'Missing shipping alloc',
  unresolved_sku: 'Unresolved SKU',
  negative_balance: 'Negative balance',
  upstream_changed: 'Upstream changed',
}

export const LEDGER_SPECIAL_STATUS_FILTER_OPTIONS: MultiSelectOption[] = [
  { id: 'any_flags', label: 'Any flags' },
  { id: 'any_errors', label: 'Any errors' },
  { id: 'no_flags', label: 'No flags' },
  { id: 'no_errors', label: 'No errors' },
]

const LEDGER_CONCRETE_STATUS_FILTER_OPTIONS: MultiSelectOption[] = (
  Object.entries(LEDGER_STATUS_LABELS) as [LedgerConcreteStatusFilterId, string][]
)
  .map(([id, label]) => ({ id, label }))
  .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))

export const LEDGER_STATUS_FILTER_OPTIONS: MultiSelectOption[] = [
  ...LEDGER_SPECIAL_STATUS_FILTER_OPTIONS,
  ...LEDGER_CONCRETE_STATUS_FILTER_OPTIONS,
]

const LEDGER_STATUS_FILTER_IDS = new Set<string>([
  ...Object.keys(LEDGER_STATUS_LABELS),
  'any_flags',
  'any_errors',
  'no_flags',
  'no_errors',
])

export const LEDGER_SIGNED_CASES_FILTER_OPTIONS: MultiSelectOption[] = [
  { id: 'nonzero', label: 'Nonzero' },
  { id: 'zero', label: 'Zero' },
]

export type LedgerFilterRow = WarehouseLedgerTransactionItem & {
  dirty?: boolean
}

function isUnitCostMissing(value: number | null | undefined): boolean {
  if (value == null) return true
  const amount = Number(value)
  return !Number.isFinite(amount)
}

function isAllocMissing(value: number | null | undefined): boolean {
  if (value == null) return true
  const amount = Number(value)
  return !Number.isFinite(amount) || amount === 0
}

export function ledgerRowGreyStatusIds(row: LedgerFilterRow): LedgerGreyStatusId[] {
  if (row.voided_at != null) return []
  const ids: LedgerGreyStatusId[] = []
  if (row.bs_changed_under_override) ids.push('upstream_changed')
  if (rowHasMeaningfulOverrides(row)) ids.push('edited')
  return ids
}

export function ledgerRowHasRedStatus(row: LedgerFilterRow, asOf?: Date): boolean {
  return ledgerRowRedStatusIds(row, asOf).length > 0
}

export function countLedgerRowsWithRedStatus(rows: LedgerFilterRow[], asOf?: Date): number {
  return rows.filter((row) => ledgerRowHasRedStatus(row, asOf)).length
}

function rowIgnoredFlagSet(row: LedgerFilterRow): Set<string> {
  return new Set(row.ignored_flags ?? [])
}

export function ledgerRowComputedRedStatusIds(row: LedgerFilterRow): LedgerRedStatusId[] {
  if (row.voided_at != null) return []

  const ids: LedgerRedStatusId[] = []
  if (Number(row.balance) < 0) {
    ids.push('negative_balance')
  }

  const kind = normalizeLedgerKind(row.kind)
  if (kind === 'manufacturer_purchase' && row.product_invoice_line_id == null) {
    ids.push('missing_linked_invoice')
  }
  if (
    (kind === 'manufacturer_purchase' ||
      kind === 'initial_store_transfer' ||
      kind === 'store_return') &&
    isUnitCostMissing(row.unit_cost)
  ) {
    ids.push('missing_case_cost')
  }
  if (kind === 'store_return' && !(row.store ?? '').trim()) {
    ids.push('missing_store')
  }
  if (kind === 'store_draw' && isAllocMissing(row.shipping_alloc)) {
    ids.push('missing_shipping_alloc')
  }
  if (kind === 'store_draw' && isAllocMissing(row.pick_fee_alloc)) {
    ids.push('missing_pick_fee_alloc')
  }
  if (
    (kind === 'manufacturer_purchase' || kind === 'initial_store_transfer') &&
    isAllocMissing(row.receiving_alloc)
  ) {
    ids.push('missing_receiving_alloc')
  }
  const sku = normalizeBulkSku(row.bk_sku)
  if (!sku || sku === 'UNKNOWN') {
    ids.push('unresolved_sku')
  }
  return ids
}

export function ledgerRowIgnoredRedStatusIds(row: LedgerFilterRow): LedgerRedStatusId[] {
  const ignored = rowIgnoredFlagSet(row)
  return ledgerRowComputedRedStatusIds(row).filter((id) => ignored.has(id))
}

export function ledgerRowRedStatusIds(
  row: LedgerFilterRow,
  asOf: Date = new Date()
): LedgerRedStatusId[] {
  const month = dateToReportMonth(row.txn_date)
  if (!uploadsErrorVisibleForMonth(month, asOf)) {
    return []
  }
  const ignored = rowIgnoredFlagSet(row)
  return ledgerRowComputedRedStatusIds(row).filter((id) => !ignored.has(id))
}

export function ledgerRowStatusIds(row: LedgerFilterRow): LedgerConcreteStatusFilterId[] {
  return [...ledgerRowGreyStatusIds(row), ...ledgerRowRedStatusIds(row)]
}

function matchesLedgerStatusFilter(row: LedgerFilterRow, selected: LedgerStatusFilterId[]): boolean {
  if (selected.length === 0) return true

  const greyIds = ledgerRowGreyStatusIds(row)
  const redIds = ledgerRowRedStatusIds(row)
  const concreteIds = [...greyIds, ...redIds]
  const hasAnyFlags = concreteIds.length > 0
  const hasAnyErrors = redIds.length > 0

  return selected.some((filterId) => {
    switch (filterId) {
      case 'any_flags':
        return hasAnyFlags
      case 'any_errors':
        return hasAnyErrors
      case 'no_flags':
        return !hasAnyFlags
      case 'no_errors':
        return !hasAnyErrors
      default:
        return concreteIds.includes(filterId)
    }
  })
}

export function ledgerStatusSortKey(row: LedgerFilterRow): string {
  const ids = ledgerRowStatusIds(row)
  if (ids.length === 0) return ''
  return ids
    .map((id) => LEDGER_STATUS_LABELS[id])
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .join('\0')
}

const KIND_LABELS: Record<WarehouseLedgerKind, string> = {
  initial_store_transfer: 'Store transfer',
  manufacturer_purchase: 'Manufacturer purchase',
  store_draw: 'Store draw',
  store_return: 'Store return',
  manual_adjustment: 'Manual adjustment',
}

const KNOWN_LEDGER_KINDS = new Set<string>(Object.keys(KIND_LABELS))

/** Map legacy/removed kinds onto the current WarehouseLedgerKind union. */
export function normalizeLedgerKind(kind: string): WarehouseLedgerKind {
  if (kind === 'initial_stock') return 'manufacturer_purchase'
  if (KNOWN_LEDGER_KINDS.has(kind)) return kind as WarehouseLedgerKind
  return 'manual_adjustment'
}

export function ledgerKindLabel(kind: WarehouseLedgerKind | string): string {
  return KIND_LABELS[normalizeLedgerKind(kind)] ?? kind
}

export function ledgerProductLabel(sku: string, productName: string | null | undefined): string {
  const name = (productName ?? '').trim()
  const skuKey = normalizeBulkSku(sku)
  if (!skuKey) return name
  return name && name !== skuKey ? `${skuKey} - ${name}` : skuKey
}

export function productForSku(
  sku: string,
  skuDisplayNames?: ReadonlyMap<string, string>,
  fallback?: string | null
): string {
  return skuDisplayNames?.get(sku) ?? fallback?.trim() ?? sku
}

function matchesDateRange(date: string, start: string, end: string): boolean {
  if (!start && !end) return true
  const rowDate = date.trim()
  if (!rowDate) return false
  if (start && rowDate < start) return false
  if (end && rowDate > end) return false
  return true
}

function matchesMultiSelect<T extends string>(value: T, selected: T[]): boolean {
  return selected.length === 0 || selected.includes(value)
}

function matchesSignedCasesFilter(
  value: number,
  filter: LedgerSignedCasesFilterId[]
): boolean {
  if (filter.length === 0) return true
  const amount = Number(value) || 0
  const isZero = amount === 0
  const isNonzero = amount !== 0
  return (
    (filter.includes('zero') && isZero) ||
    (filter.includes('nonzero') && isNonzero)
  )
}

function sortLabels(labels: Iterable<string>): string[] {
  return Array.from(labels).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
}

export function hasActiveLedgerTableFilters(filters: LedgerTableFilters): boolean {
  return (
    filters.status.length > 0 ||
    filters.bk_txn_id.length > 0 ||
    filters.bs_log_id.length > 0 ||
    filters.date_from !== '' ||
    filters.date_to !== '' ||
    filters.product.length > 0 ||
    filters.linked_invoice.length > 0 ||
    filters.kind.length > 0 ||
    filters.signed_cases.length > 0 ||
    filters.order_number.length > 0 ||
    hasActiveAmountFilter(filters.unit_cost, filters.unit_cost_op) ||
    filters.notes.trim() !== ''
  )
}

export function migrateLedgerTableFilters(raw: Partial<LedgerTableFilters> & Record<string, unknown>): LedgerTableFilters {
  const next: LedgerTableFilters = {
    ...EMPTY_LEDGER_TABLE_FILTERS,
    ...raw,
    status: Array.isArray(raw.status)
      ? raw.status.filter(
          (value): value is LedgerStatusFilterId =>
            typeof value === 'string' && LEDGER_STATUS_FILTER_IDS.has(value)
        )
      : [],
    bk_txn_id: Array.isArray(raw.bk_txn_id) ? raw.bk_txn_id.map(String) : [],
    bs_log_id: Array.isArray(raw.bs_log_id) ? raw.bs_log_id.map(String) : [],
    product: Array.isArray(raw.product) ? raw.product.map(String) : [],
    linked_invoice: Array.isArray(raw.linked_invoice) ? raw.linked_invoice.map(String) : [],
    kind: Array.isArray(raw.kind)
      ? Array.from(
          new Set(
            raw.kind
              .filter((value) => typeof value === "string")
              .map((value) => normalizeLedgerKind(value))
          )
        )
      : [],
    order_number: Array.isArray(raw.order_number) ? raw.order_number.map(String) : [],
    date_from: typeof raw.date_from === 'string' ? raw.date_from : '',
    date_to: typeof raw.date_to === 'string' ? raw.date_to : '',
    notes: typeof raw.notes === 'string' ? raw.notes : '',
    unit_cost_op:
      raw.unit_cost_op === 'gt' ||
      raw.unit_cost_op === 'gte' ||
      raw.unit_cost_op === 'lt' ||
      raw.unit_cost_op === 'lte'
        ? raw.unit_cost_op
        : 'eq',
    unit_cost: typeof raw.unit_cost === 'string' ? raw.unit_cost : '',
  }

  if (Array.isArray(raw.signed_cases)) {
    next.signed_cases = raw.signed_cases.filter(
      (value): value is LedgerSignedCasesFilterId => value === 'zero' || value === 'nonzero'
    )
  } else if (raw.signed_cases === 'zero' || raw.signed_cases === 'nonzero') {
    next.signed_cases = [raw.signed_cases]
  }

  if (typeof raw.unit_cost === 'string' && raw.unit_cost !== 'has' && raw.unit_cost !== 'missing') {
    next.unit_cost = raw.unit_cost
  }

  return next
}

export function buildLedgerProductOptions(
  rows: LedgerFilterRow[],
  skuDisplayNames?: ReadonlyMap<string, string>
): MultiSelectOption[] {
  const ledgerProductBySku = new Map<string, string | null>()
  for (const row of rows) {
    const sku = normalizeBulkSku(row.bk_sku)
    if (!sku) continue
    if (!ledgerProductBySku.has(sku)) {
      ledgerProductBySku.set(sku, row.product_name)
    }
  }
  return Array.from(ledgerProductBySku.entries())
    .map(([id, productName]) => ({
      id,
      label: ledgerProductLabel(id, productForSku(id, skuDisplayNames, productName)),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
}

export function buildLedgerKindOptions(rows: LedgerFilterRow[]): MultiSelectOption[] {
  const kinds = new Set<WarehouseLedgerKind>()
  for (const row of rows) {
    const kind = normalizeLedgerKind(row.kind)
    if (KNOWN_LEDGER_KINDS.has(kind)) kinds.add(kind)
  }
  return Array.from(kinds)
    .sort((a, b) => ledgerKindLabel(a).localeCompare(ledgerKindLabel(b)))
    .map((id) => ({ id, label: ledgerKindLabel(id) }))
}

export function shortLedgerBkTxnId(bkTxnId: string): string {
  const trimmed = bkTxnId.trim()
  if (!trimmed) return trimmed
  const dash = trimmed.indexOf('-')
  return dash > 0 ? trimmed.slice(0, dash) : trimmed
}

export function buildLedgerBkTxnIdOptions(rows: LedgerFilterRow[]): MultiSelectOption[] {
  const ids = new Set<string>()
  for (const row of rows) {
    const id = row.bk_txn_id.trim()
    if (id) ids.add(id)
  }
  return sortLabels(ids).map((id) => ({
    id,
    label: shortLedgerBkTxnId(id),
  }))
}

export function linkedInvoiceFilterValue(
  row: Pick<LedgerFilterRow, 'kind' | 'product_invoice_line_id'>,
  invoiceNumberByLineId?: Map<number, string>
): string {
  if (row.kind === 'initial_store_transfer') return LEDGER_LINKED_INVOICE_STORE_TRANSFER
  if (row.product_invoice_line_id == null) return LEDGER_LINKED_INVOICE_NONE
  return (
    invoiceNumberByLineId?.get(row.product_invoice_line_id) ??
    String(row.product_invoice_line_id)
  )
}

export function buildLedgerLinkedInvoiceOptions(
  rows: LedgerFilterRow[],
  invoiceNumberByLineId?: Map<number, string>
): MultiSelectOption[] {
  const options = new Map<string, string>()
  for (const row of rows) {
    const id = linkedInvoiceFilterValue(row, invoiceNumberByLineId)
    if (id === LEDGER_LINKED_INVOICE_STORE_TRANSFER) {
      options.set(id, 'No invoice expected')
    } else if (id === LEDGER_LINKED_INVOICE_NONE) {
      options.set(id, 'Not linked')
    } else {
      options.set(id, id)
    }
  }
  return Array.from(options.entries())
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
}

export function buildLedgerBsLogIdOptions(rows: LedgerFilterRow[]): MultiSelectOption[] {
  const ids = new Set<string>()
  for (const row of rows) {
    if (row.bs_log_id != null) ids.add(String(row.bs_log_id))
  }
  return sortLabels(ids).map((id) => ({ id, label: id }))
}

export function buildLedgerOrderNumberOptions(rows: LedgerFilterRow[]): MultiSelectOption[] {
  const orders = new Set<string>()
  for (const row of rows) {
    const order = (row.order_number ?? '').trim()
    if (order) orders.add(order)
  }
  return sortLabels(orders).map((id) => ({ id, label: id }))
}

export function applyLedgerFilters<T extends LedgerFilterRow>(
  rows: T[],
  filters: LedgerTableFilters,
  options?: { invoiceNumberByLineId?: Map<number, string> }
): T[] {
  return rows.filter((row) => {
    if (!matchesLedgerStatusFilter(row, filters.status)) return false
    if (!matchesMultiSelect(row.bk_txn_id, filters.bk_txn_id)) return false
    if (row.bs_log_id == null) {
      if (filters.bs_log_id.length > 0) return false
    } else if (!matchesMultiSelect(String(row.bs_log_id), filters.bs_log_id)) {
      return false
    }
    if (!matchesDateRange(row.txn_date, filters.date_from, filters.date_to)) return false

    const sku = normalizeBulkSku(row.bk_sku)
    if (filters.product.length > 0) {
      const selected = filters.product.map((value) => normalizeBulkSku(value))
      if (!selected.includes(sku)) return false
    }

    if (filters.linked_invoice.length > 0) {
      const linkedValue = linkedInvoiceFilterValue(row, options?.invoiceNumberByLineId)
      if (!filters.linked_invoice.includes(linkedValue)) return false
    }

    if (
      filters.kind.length > 0 &&
      !filters.kind.includes(normalizeLedgerKind(row.kind))
    ) {
      return false
    }

    if (!matchesSignedCasesFilter(row.signed_cases, filters.signed_cases)) return false

    const orderNumber = (row.order_number ?? '').trim()
    if (orderNumber) {
      if (!matchesMultiSelect(orderNumber, filters.order_number)) return false
    } else if (filters.order_number.length > 0) {
      return false
    }

    if (!matchesNumericFilter(row.unit_cost ?? null, filters.unit_cost, filters.unit_cost_op)) {
      return false
    }

    if (!containsText(row.notes ?? '', filters.notes)) return false

    return true
  })
}

export function ledgerSortValue(row: LedgerFilterRow, key: string): unknown {
  switch (key) {
    case 'status':
      return ledgerStatusSortKey(row)
    case 'bk_txn_id':
      return row.bk_txn_id
    case 'bs_log_id':
      return row.bs_log_id ?? Number.NEGATIVE_INFINITY
    case 'txn_date':
      return row.txn_date
    case 'bk_sku':
      return normalizeBulkSku(row.bk_sku)
    case 'product_name':
      return row.product_name ?? ''
    case 'kind':
      return row.kind
    case 'store':
      return row.store ?? ''
    case 'product_invoice_line_id':
      return row.product_invoice_line_id ?? Number.NEGATIVE_INFINITY
    case 'signed_cases':
      return Number(row.signed_cases) || 0
    case 'balance':
      return Number(row.balance) || 0
    case 'order_number':
      return row.order_number ?? ''
    case 'username':
      return row.username ?? ''
    case 'unit_cost':
      return row.unit_cost ?? Number.NEGATIVE_INFINITY
    case 'shipping_alloc':
      return Number(row.shipping_alloc) || 0
    case 'pick_fee_alloc':
      return Number(row.pick_fee_alloc) || 0
    case 'receiving_alloc':
      return Number(row.receiving_alloc) || 0
    case 'notes':
      return row.notes ?? ''
    default:
      return (row as Record<string, unknown>)[key]
  }
}

export const LEDGER_KIND_OPTIONS: { value: WarehouseLedgerKind; label: string }[] = (
  Object.keys(KIND_LABELS) as WarehouseLedgerKind[]
).map((value) => ({ value, label: KIND_LABELS[value] }))

export const LEDGER_STORE_LOCATIONS = ['Highland Park', 'River Oaks'] as const

export type LedgerLockedField =
  | 'order_number'
  | 'unit_cost'
  | 'shipping_alloc'
  | 'pick_fee_alloc'
  | 'receiving_alloc'
  | 'linked_invoice'
  | 'store'

export type LedgerLockContext = Pick<LedgerFilterRow, 'product_invoice_line_id'>

export function isLedgerFieldLocked(
  kind: WarehouseLedgerKind,
  field: LedgerLockedField,
  context?: LedgerLockContext | null,
): boolean {
  switch (kind) {
    case 'manufacturer_purchase':
      if (field === 'unit_cost' && context?.product_invoice_line_id != null) {
        return true
      }
      return (
        field === 'order_number' ||
        field === 'shipping_alloc' ||
        field === 'pick_fee_alloc' ||
        field === 'receiving_alloc' ||
        field === 'store'
      )
    case 'manual_adjustment':
      return (
        field === 'order_number' ||
        field === 'shipping_alloc' ||
        field === 'pick_fee_alloc' ||
        field === 'store'
      )
    case 'initial_store_transfer':
      return (
        field === 'order_number' ||
        field === 'shipping_alloc' ||
        field === 'pick_fee_alloc' ||
        field === 'receiving_alloc' ||
        field === 'linked_invoice'
      )
    case 'store_draw':
      return field === 'unit_cost'
    case 'store_return':
      return (
        field === 'shipping_alloc' ||
        field === 'pick_fee_alloc' ||
        field === 'receiving_alloc'
      )
    default:
      return false
  }
}

export { sortLabels }
